import _ from 'lodash';
import { Country, ErrorType, RE_EMAIL, State as StateEnum, } from '@tradingblock/types';
import { BeneficiaryAccountType } from '@tradingblock/types';
// letters, periods, apostrophes, hyphens, slashes, or spaces
export const RE_VALID_PERSON_NAME = /^[A-Za-z.'/-]+(?: [A-Za-z.'/-]+)*$/;
export const isValidPersonName = (value) => {
    const trimmed = (value || '').trim();
    return trimmed.length > 0 && RE_VALID_PERSON_NAME.test(trimmed);
};
// PO Box addresses not allowed for home address
export const RE_PO_BOX = /^ *((#\d+)|((box|bin)[-. \/\\]?\d+)|(.*p[ \.]? ?(o|0)[-. \/\\]? *-?((box|bin)|b|(#|num)?\d+))|(p(ost)? *(o(ff(ice)?)?)? *((box|bin)|b)? *\d+)|(p *-?\/?(o)? *-?box)|post office box|((box|bin)|b) *(number|num|#)? *\d+|(num|number|#) *\d+)/i;
// Check if address input is in the correct ASCII Range
export const RE_POSTAL_CODE = /^\d{5}(-\d{4})?$/;
// Check if address input is in the correct ASCII Range
export const ASCII_AUDIT_RANGE = /^[\u0020-\u007E]+$/;
const getError = (value, error) => {
    if (!value && !_.isNumber(value)) {
        return error;
    }
    return undefined;
};
const getRequiredError = (value) => {
    return getError(value, ErrorType.Required);
};
const getInvalidError = (value) => {
    return getError(value, ErrorType.Invalid);
};
const getMismatchError = (value) => {
    return getError(value, ErrorType.Mismatch);
};
const getEmailError = (value) => {
    return getRequiredError(value) || getInvalidError(value && RE_EMAIL.test(value));
};
const getInvalidLengthError = (value) => {
    return getError(value, ErrorType.InvalidLength);
};
// USPS Address Verification using new backend REST endpoint
const uspsVerification = async (address, tradingApiUrl, apiToken) => {
    const { address1, address2, city, state, postalCode, country } = address;
    // If address has been provided, excluding postal code, and country is US, then perform USPS verification
    try {
        if (address1 && city && state && country === Country.UnitedStatesOfAmerica && state !== StateEnum.PuertoRico) {
            // Don't make API call without authentication token
            if (!apiToken) {
                console.warn('USPS verification skipped: API token not provided');
                return;
            }
            // Build query parameters with %20 encoding for spaces
            const queryParams = [];
            queryParams.push(`address1=${encodeURIComponent((address1 || '').trim())}`);
            queryParams.push(`city=${encodeURIComponent((city || '').trim())}`);
            queryParams.push(`state=${encodeURIComponent((state || '').trim())}`);
            // Add address2 if provided
            if (address2) {
                queryParams.push(`address2=${encodeURIComponent(address2.trim())}`);
            }
            const queryString = queryParams.join('&');
            // Build request headers - always include token (we've verified it exists above)
            const headers = {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: apiToken,
            };
            const response = await fetch(`${tradingApiUrl}/USPSAddress?${queryString}`, {
                method: 'GET',
                headers,
            });
            if (!response.ok) {
                // Handle HTTP errors - use layman-friendly message for users
                return {
                    error: {
                        number: response.status.toString(),
                        source: 'HTTP',
                        description: "We couldn't verify this address. Please check your information and try again.",
                    },
                };
            }
            const jsonResult = await response.json();
            // Check ResponseCode (0 = success, non-zero = error)
            if (jsonResult.ResponseCode !== 0) {
                // Handle different error response formats - use layman-friendly messages
                let errorDescription = "We couldn't verify this address. Please check your information and try again.";
                if (jsonResult.Payload) {
                    if (jsonResult.Payload.error) {
                        errorDescription = jsonResult.Payload.error;
                    }
                    else if (typeof jsonResult.Payload === 'string') {
                        errorDescription = jsonResult.Payload;
                    }
                    else if (jsonResult.Payload.description) {
                        errorDescription = jsonResult.Payload.description;
                    }
                    else if (jsonResult.Payload.message) {
                        errorDescription = jsonResult.Payload.message;
                    }
                }
                else if (jsonResult.error) {
                    errorDescription =
                        typeof jsonResult.error === 'string'
                            ? jsonResult.error
                            : jsonResult.error.description || 'Address validation failed';
                }
                else if (jsonResult.message) {
                    errorDescription = jsonResult.message;
                }
                return {
                    error: {
                        number: (jsonResult.ResponseCode && jsonResult.ResponseCode.toString()) || '0',
                        source: 'USPS',
                        description: errorDescription,
                    },
                };
            }
            const payload = jsonResult.Payload;
            if (!payload) {
                return {
                    error: {
                        number: '0',
                        source: 'USPS',
                        description: "We couldn't verify this address. Please try again.",
                    },
                };
            }
            // Split postalCode into zip5 and zip4
            let zip5 = '';
            let zip4 = '';
            if (payload.postalCode) {
                const zipParts = payload.postalCode.split('-');
                zip5 = zipParts[0] || '';
                zip4 = zipParts[1] || '';
            }
            // Return the standardized address components
            // Note: New API doesn't return address2 separately, so we set it to null
            return {
                city: payload.city || '',
                state: payload.state || '',
                zip5: zip5,
                zip4: zip4,
                uspsAddress1: payload.address1 || '',
                uspsAddress2: null,
                isPoBox: payload.isPoBox || false,
                isSecondaryInformationRequired: payload.isSecondaryInformationRequired || false,
                isSecondaryInformationNotRecognized: payload.isSecondaryInformationNotRecognized || false,
            };
        }
    }
    catch (err) {
        console.error('USPS verification error:', err);
        return {
            error: {
                number: '0',
                source: 'Network',
                description: err instanceof Error ? err.message : 'Network error occurred',
            },
        };
    }
};
// Function to get USPS response from storage
const getUspsResponseFromStorage = (prefix) => {
    const uspsResponseFromStorage = sessionStorage.getItem(`${prefix.split('.')[0]}UspsResponse`);
    return uspsResponseFromStorage && uspsResponseFromStorage !== 'undefined' ? uspsResponseFromStorage : null;
};
// Async function to handle USPS verification
const getUSPS = async (address, tradingApiUrl, apiToken) => {
    await new Promise(resolve => setTimeout(resolve, 100));
    return await uspsVerification(address, tradingApiUrl, apiToken);
};
// Create a unique key for an address to use for caching
const createAddressKey = (address) => {
    const normalized = {
        address1: (address.address1 || '').trim().toUpperCase(),
        address2: (address.address2 || '').trim().toUpperCase(),
        city: (address.city || '').trim().toUpperCase(),
        state: (address.state || '').trim().toUpperCase(),
        country: (address.country || '').trim().toUpperCase(),
    };
    return `${normalized.address1}|${normalized.address2}|${normalized.city}|${normalized.state}|${normalized.country}`;
};
// In-memory cache for pending requests to prevent duplicate concurrent calls
const pendingRequests = new Map();
// Debounce timers map - keyed by address key
const debounceTimers = new Map();
// Debounce delay in milliseconds
const DEBOUNCE_DELAY = 500;
// Exported function to trigger USPS verification independently for debounced validation
// This can be called from components with debounced validation
export const triggerUspsVerification = async (address, prefix, tradingApiUrl, apiToken) => {
    const { address1, address2, city, state, postalCode, country } = address;
    // Check if validation should run (US country, required fields present, not Puerto Rico)
    if (!address1 || !city || !state || country !== Country.UnitedStatesOfAmerica || state === StateEnum.PuertoRico) {
        // Clear USPS storage if fields are cleared
        if (!address1 || !city) {
            // Handle both formats: with dot separator (Address.tsx) and without (validateAddress)
            const storageKey = prefix.endsWith('.') ? `${prefix}uspsResponse` : `${prefix.split('.')[0]}UspsResponse`;
            sessionStorage.setItem(storageKey, 'undefined');
        }
        // Clear any pending debounce timer
        const addressKey = createAddressKey({ address1, address2, city, state, country });
        const existingTimer = debounceTimers.get(addressKey);
        if (existingTimer) {
            clearTimeout(existingTimer);
            debounceTimers.delete(addressKey);
        }
        return null;
    }
    // Don't make API call without authentication token
    if (!apiToken) {
        console.warn('USPS verification skipped: API token not provided');
        return null;
    }
    // Create address key for caching
    const addressKey = createAddressKey({ address1, address2, city, state, country });
    // Check if we already have a cached result for this exact address
    const storageKey = prefix.endsWith('.') ? `${prefix}uspsResponse` : `${prefix.split('.')[0]}UspsResponse`;
    const cachedResponse = sessionStorage.getItem(storageKey);
    if (cachedResponse && cachedResponse !== 'undefined') {
        try {
            const cached = JSON.parse(cachedResponse);
            // Check if cached result includes the address key or matches the current address
            if (cached.addressKey === addressKey ||
                (cached.uspsAddress1 &&
                    cached.uspsAddress1.toUpperCase() === (address1 || '').trim().toUpperCase() &&
                    cached.city &&
                    cached.city.toUpperCase() === (city || '').trim().toUpperCase() &&
                    cached.state &&
                    cached.state.toUpperCase() === (state || '').trim().toUpperCase())) {
                // We have a valid cached result for this address - return it without making API call
                // Clear any pending debounce timer since we have a cached result
                const existingTimer = debounceTimers.get(addressKey);
                if (existingTimer) {
                    clearTimeout(existingTimer);
                    debounceTimers.delete(addressKey);
                }
                return cached;
            }
        }
        catch (e) {
            // Invalid cached data, continue to make API call
        }
    }
    // Check if there's already a pending request for this exact address
    if (pendingRequests.has(addressKey)) {
        // Return the existing promise to avoid duplicate requests
        return pendingRequests.get(addressKey);
    }
    // Clear any existing debounce timer for this address
    const existingTimer = debounceTimers.get(addressKey);
    if (existingTimer) {
        clearTimeout(existingTimer);
    }
    // Create a promise that will resolve after debounce delay
    const debouncedPromise = new Promise(resolve => {
        const timer = setTimeout(async () => {
            // Remove timer from map
            debounceTimers.delete(addressKey);
            // Now actually make the API call
            const requestPromise = (async () => {
                try {
                    const addressModel = {
                        address1: address1 || '',
                        address2: address2 || '',
                        city: city || '',
                        state: state || '',
                        postalCode: postalCode || '',
                        country: country || '',
                    };
                    const result = await uspsVerification(addressModel, tradingApiUrl, apiToken);
                    if (result) {
                        // Store the address key with the result for future comparison
                        const resultWithKey = Object.assign({}, result, { addressKey });
                        // Store result in sessionStorage
                        sessionStorage.setItem(storageKey, JSON.stringify(resultWithKey));
                        return resultWithKey;
                    }
                    return result;
                }
                catch (err) {
                    console.error(err);
                    return null;
                }
                finally {
                    // Remove from pending requests after completion
                    pendingRequests.delete(addressKey);
                }
            })();
            // Store the pending request
            pendingRequests.set(addressKey, requestPromise);
            // Resolve with the request promise
            resolve(requestPromise);
        }, DEBOUNCE_DELAY);
        // Store the debounce timer
        debounceTimers.set(addressKey, timer);
    });
    return debouncedPromise;
};
// Function to validate the address using the USPS response
const validateWithUspsResponse = (uspsResponse, address, prefix, tradingApiUrl, apiToken, isTrustedContactAddress = false) => {
    let isValidAddress = false;
    let uspsResponseValue;
    // Destructuring address object to get individual address components
    const { address1, address2, city, state, country } = address;
    if (state === StateEnum.PuertoRico) {
        return {};
    }
    // Check if a USPS response exists and is not 'undefined'
    if (uspsResponse && uspsResponse !== 'undefined') {
        // Parse the USPS response
        uspsResponseValue = JSON.parse(uspsResponse);
        // Extract relevant data from the parsed USPS response
        const { city, state, zip5, error, uspsAddress1, uspsAddress2, isPoBox, isSecondaryInformationRequired, isSecondaryInformationNotRecognized, } = uspsResponseValue;
        // Check PO Box restriction (unless it's a trusted contact address)
        if (isPoBox && !isTrustedContactAddress) {
            isValidAddress = false; // PO Box addresses are not valid for non-trusted contact addresses
        }
        // Format the city name for comparison
        const formattedCityCapitalized = _.capitalize(city);
        const startCaseCity = _.startCase(formattedCityCapitalized);
        // Compare each component of the address with the USPS response
        const isCityEqual = _.toLower(startCaseCity) === _.toLower(address.city);
        // Extract zip5 from postalCode (handle zip+4 format like "60606-6604")
        const userZip5 = address.postalCode ? address.postalCode.split('-')[0].trim() : '';
        const isZipEqual = zip5 === userZip5;
        const isStateEqual = state === address.state;
        // Normalize addresses for comparison (trim, uppercase, normalize whitespace)
        const normalizeAddress = (addr) => {
            if (!addr)
                return '';
            return addr
                .trim()
                .replace(/\s+/g, ' ')
                .toUpperCase();
        };
        const normalizedUspsAddress1 = normalizeAddress(uspsAddress1);
        const normalizedUserAddress1 = normalizeAddress(address.address1);
        const normalizedUspsAddress2 = normalizeAddress(uspsAddress2);
        const normalizedUserAddress2 = normalizeAddress(address.address2);
        // Since Smarty doesn't return address2 separately, only validate address1
        // Address2 validation is optional - if Smarty doesn't have it, don't fail validation
        const isAddressEqual = normalizedUspsAddress1 === normalizedUserAddress1 &&
            (!normalizedUspsAddress2 || normalizedUspsAddress2 === normalizedUserAddress2);
        const isAddressCorrect = !error && isCityEqual && isZipEqual && isStateEqual && isAddressEqual;
        // Set the flag based on the address comparison results
        isValidAddress = isAddressCorrect;
    }
    // Clear USPS storage if address fields are cleared
    if (!address1 || !city) {
        sessionStorage.setItem(`${prefix.split('.')[0]}UspsResponse`, 'undefined');
    }
    // If address has been provided, excluding postal code, country is US but the current address is not valid, then perform USPS verification to retrieve valid address items or errors
    if ((address1 && city && state && country === Country.UnitedStatesOfAmerica && !isValidAddress) ||
        (address1 && address2 && city && state && country === Country.UnitedStatesOfAmerica && !isValidAddress)) {
        // Set USPS Verification result to sessionStorage
        getUSPS(address, tradingApiUrl, apiToken).then(result => {
            sessionStorage.setItem(`${prefix.split('.')[0]}UspsResponse`, JSON.stringify(result));
            uspsResponseValue = result;
        });
    }
    if (uspsResponseValue && address.country === Country.UnitedStatesOfAmerica) {
        const { city, state, zip5, error, uspsAddress1, uspsAddress2, isPoBox, isSecondaryInformationRequired, isSecondaryInformationNotRecognized, } = uspsResponseValue;
        if (error && address.address1 && address.city && address.state && address.postalCode) {
            return {
                [`${prefix}postalCode`]: `${error.description.trim()} Please review the entered address.`,
                [`${prefix}city`]: `${error.description.trim()} Please review the entered address.`,
                [`${prefix}state`]: `${error.description.trim()} Please review the entered address.`,
                [`${prefix}address1`]: `${error.description.trim()} Please review the entered address.`,
            };
        }
        if (!address.address1 || !address.city) {
            sessionStorage.setItem(`${prefix.split('.')[0]}UspsResponse`, 'undefined');
        }
        if (city && state && zip5) {
            // Formatting for error message display of USPS response
            const formattedCityCapitalized = _.capitalize(city);
            const startCaseCity = _.startCase(formattedCityCapitalized);
            const isCityEqual = _.toLower(startCaseCity) === _.toLower(address.city);
            // Extract zip5 from postalCode (handle zip+4 format like "60606-6604")
            const userZip5 = address.postalCode ? address.postalCode.split('-')[0].trim() : '';
            const isZipEqual = zip5 === userZip5;
            const isStateEqual = state === address.state;
            // Normalize addresses for comparison (trim, uppercase, normalize whitespace)
            const normalizeAddress = (addr) => {
                if (!addr)
                    return '';
                return addr
                    .trim()
                    .replace(/\s+/g, ' ')
                    .toUpperCase();
            };
            const normalizedUspsAddress1 = normalizeAddress(uspsAddress1);
            const normalizedUserAddress1 = normalizeAddress(address.address1);
            const normalizedUspsAddress2 = normalizeAddress(uspsAddress2);
            const normalizedUserAddress2 = normalizeAddress(address.address2);
            const isAddress1Equal = normalizedUspsAddress1 === normalizedUserAddress1;
            // Since Smarty doesn't return address2 separately, only validate if both exist
            // If Smarty doesn't have address2, don't validate user's address2
            const isAddress2Equal = normalizedUspsAddress2 ? normalizedUspsAddress2 === normalizedUserAddress2 : true;
            const formattedAddress1 = uspsAddress1
                .toLowerCase()
                .split(' ')
                .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
                .join(' ');
            const formattedAddress2 = uspsAddress2
                ? uspsAddress2
                    .toLowerCase()
                    .split(' ')
                    .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
                    .join(' ')
                : null;
            // Required Errors
            if (!address.postalCode) {
                return {
                    [`${prefix}postalCode`]: getRequiredError(address.postalCode),
                };
            }
            if (!address.city) {
                return {
                    [`${prefix}city`]: getRequiredError(address.city),
                };
            }
            // USPS Verification Errors
            let errors = {};
            // PO Box validation - if Smarty identifies this as a PO Box, show error
            if (isPoBox && !isTrustedContactAddress) {
                errors = Object.assign({}, errors, { [`${prefix}address1`]: 'PO Box addresses are not allowed for this address type.' });
            }
            // Secondary information (address2) required validation
            if (isSecondaryInformationRequired && !address.address2) {
                errors = Object.assign({}, errors, { [`${prefix}address2`]: 'Secondary address information (apartment, suite, etc.) is required for this address.' });
            }
            // Secondary information not recognized warning
            if (isSecondaryInformationNotRecognized && address.address2) {
                errors = Object.assign({}, errors, { [`${prefix}address2`]: 'The secondary address information provided was not recognized. Please verify or remove it.' });
            }
            if (!isCityEqual) {
                errors = Object.assign({}, errors, { [`${prefix}city`]: `Please verify city. USPS Provided City: ${startCaseCity}` });
            }
            if (!isZipEqual) {
                errors = Object.assign({}, errors, { [`${prefix}postalCode`]: `Please verify the zip code. USPS Provided Zip Code: ${zip5}` });
            }
            if (!isStateEqual) {
                errors = Object.assign({}, errors, { [`${prefix}state`]: `Please verify the state. USPS Provided State: ${state}` });
            }
            // Only show address1 error if we haven't already shown a more specific error above
            if (!isAddress1Equal && !errors[`${prefix}address1`]) {
                errors = Object.assign({}, errors, { [`${prefix}address1`]: `Please utilize the USPS provided formatting: ${formattedAddress1}` });
            }
            if (normalizedUspsAddress2 && !isAddress2Equal) {
                errors = Object.assign({}, errors, { [`${prefix}address2`]: `Please utilize the USPS provided formatting: ${formattedAddress2}` });
            }
            return errors;
        }
    }
};
export const validateAddress = (address, tradingApiUrl, apiToken, field) => {
    const prefix = `${field || 'address'}.`;
    const isMailing = _.toLower(prefix).includes('mailing');
    const stateOrPostalCodeRequired = address.country === Country.UnitedStatesOfAmerica;
    const isTrustedContactAddress = field && field.toLowerCase().includes('trustedcontact');
    let uspsResponseFromStorage = getUspsResponseFromStorage(prefix.split('.')[0]);
    const uspsValidationErrors = validateWithUspsResponse(uspsResponseFromStorage, address, prefix, tradingApiUrl, apiToken, isTrustedContactAddress);
    if (Object.keys(uspsValidationErrors || {}).length > 0) {
        return uspsValidationErrors || {};
    }
    if (!isMailing) {
        const errors = {
            [`${prefix}country`]: getRequiredError(address.country),
            [`${prefix}address1`]: getRequiredError(address.address1) ||
                (!isTrustedContactAddress && getInvalidError(!RE_PO_BOX.test(address.address1))) ||
                getInvalidError(ASCII_AUDIT_RANGE.test(address.address1)) ||
                getInvalidLengthError(address.address1.length <= 30) ||
                (address.address1 && getInvalidError(_.trim(address.address1) !== '')),
            [`${prefix}postalCode`]: getRequiredError(stateOrPostalCodeRequired ? address.postalCode : true) ||
                getInvalidError(stateOrPostalCodeRequired ? RE_POSTAL_CODE.test(address.postalCode) : true),
            [`${prefix}city`]: getRequiredError(address.city),
            [`${prefix}state`]: getRequiredError(stateOrPostalCodeRequired ? address.state : true),
        };
        // Only validate address2 if it has a value
        if (address.address2 && address.address2.trim() !== '') {
            errors[`${prefix}address2`] =
                (!isTrustedContactAddress && getInvalidError(!RE_PO_BOX.test(address.address2))) ||
                    getInvalidError(ASCII_AUDIT_RANGE.test(address.address2)) ||
                    getInvalidLengthError(address.address2.length <= 30) ||
                    getInvalidError(_.trim(address.address2) !== '');
        }
        // Remove any undefined errors
        Object.keys(errors).forEach(key => errors[key] === undefined && delete errors[key]);
        return errors;
    }
    else if (isMailing) {
        const errors = {
            [`${prefix}country`]: getRequiredError(address.country),
            [`${prefix}address1`]: getRequiredError(address.address1) || (address.address1 && getInvalidError(_.trim(address.address1) !== '')),
            [`${prefix}postalCode`]: getRequiredError(stateOrPostalCodeRequired ? address.postalCode : true),
            [`${prefix}city`]: getRequiredError(address.city),
            [`${prefix}state`]: getRequiredError(stateOrPostalCodeRequired ? address.state : true),
        };
        // Only validate address2 if it has a value
        if (address.address2 && address.address2.trim() !== '') {
            errors[`${prefix}address2`] = getInvalidError(_.trim(address.address2) !== '');
        }
        // Remove any undefined errors
        Object.keys(errors).forEach(key => errors[key] === undefined && delete errors[key]);
        return errors;
    }
};
const validatePhoneNumber = (value) => {
    // Correct the regex pattern to allow multiple digits, underscores, and hyphens
    let regex = /^[0-9_-]+$/;
    // Check if the value matches the regex and if its length (not including "-") is greater than 10
    return regex.test(value) && value.replace(/-/g, '').length >= 10;
};
export const validateTrustedContactFields = (values, tradingApiUrl, apiToken) => {
    return Object.assign({ 'trustedContact.firstName': getRequiredError(values.trustedContact.firstName) ||
            getInvalidError(isValidPersonName(values.trustedContact.firstName)) ||
            getInvalidLengthError(values.trustedContact.firstName.length <= 20), 'trustedContact.lastName': getRequiredError(values.trustedContact.lastName) ||
            getInvalidError(isValidPersonName(values.trustedContact.lastName)) ||
            getInvalidLengthError(values.trustedContact.lastName.length <= 20) }, validateAddress(values.trustedContact.address, tradingApiUrl, apiToken, 'trustedContact.address'), { 'trustedContact.email': getEmailError(values.trustedContact.email), 'trustedContact.phones[0].phoneNumber': getRequiredError(values.trustedContact.phones[0].phoneNumber) ||
            getInvalidError(validatePhoneNumber(values.trustedContact.phones[0].phoneNumber)) });
};
export const validateBeneficiaryPercentages = (beneficiaries) => {
    const primaryBeneficiaries = beneficiaries.filter(b => b.beneficiaryAccountType === BeneficiaryAccountType.Primary);
    const contingentBeneficiaries = beneficiaries.filter(b => b.beneficiaryAccountType === BeneficiaryAccountType.Contingent);
    const totalPrimaryPercent = _.sumBy(primaryBeneficiaries, b => _.toNumber(b.percent));
    const totalContingentPercent = _.sumBy(contingentBeneficiaries, b => _.toNumber(b.percent));
    const errors = {};
    // Block any contingent beneficiaries if no primary exists
    if (contingentBeneficiaries.length > 0) {
        if (primaryBeneficiaries.length === 0) {
            errors['beneficiaries[0].beneficiaryAccountType'] =
                'At least one primary beneficiary is required before adding contingent beneficiaries.';
            errors['beneficiaries[0].percent'] =
                'At least one primary beneficiary is required before adding contingent beneficiaries.';
            return errors;
        }
        if (totalPrimaryPercent !== 100) {
            primaryBeneficiaries.forEach((b, i) => {
                const index = beneficiaries.indexOf(b);
                errors[`beneficiaries[${index}].percent`] = 'Primary beneficiaries must total 100%.';
            });
            return errors;
        }
        if (totalContingentPercent !== 100) {
            contingentBeneficiaries.forEach((b, i) => {
                const index = beneficiaries.indexOf(b);
                errors[`beneficiaries[${index}].percent`] = 'Contingent beneficiaries must total 100%.';
            });
            return errors;
        }
    }
    // Validate primary percentages
    if (primaryBeneficiaries.length > 0) {
        if (totalPrimaryPercent > 100) {
            primaryBeneficiaries.forEach(b => {
                const index = beneficiaries.indexOf(b);
                errors[`beneficiaries[${index}].percent`] = 'Primary beneficiaries cannot exceed 100%.';
            });
        }
        else if (totalPrimaryPercent < 100 && primaryBeneficiaries.length > 0) {
            primaryBeneficiaries.forEach(b => {
                const index = beneficiaries.indexOf(b);
                errors[`beneficiaries[${index}].percent`] = 'Primary beneficiaries must total 100%.';
            });
        }
    }
    return errors;
};
export const validateTwoFactorAuthenticationSecurityCode = (securityCodeString) => {
    if (/^\d{6}$/.test(securityCodeString)) {
        return true;
    }
    return false;
};
//# sourceMappingURL=validation.js.map